import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, Package, Percent, Route, Target, TrendingUp, Users as UsersIcon, Zap } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/apiMode';
import CapexConfigurator from './CapexConfigurator';
import OpexCalculator from './OpexCalculator';
import BusinessPlan from './BusinessPlan';
import Wiki from './Wiki';
import Settings from './Settings';
import FollowupPage from './FollowupPage';
import AdminPartnerSection from './AdminPartnerSection';
import AdminFAQSection from './AdminFAQSection';
import AdminContentSection from './AdminContentSection';

const tdStyle = { padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem', verticalAlign: 'top' };
const thStyle = { ...tdStyle, background: '#f8fafc', fontWeight: 700, textAlign: 'left', color: '#334155', fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.05em' };
const chartColors = ['#1d4ed8', '#0f766e', '#f59e0b', '#db2777', '#16a34a', '#7c3aed', '#ea580c'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, logout } = useApp();

  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [capex, setCapex] = useState([]);
  const [bps, setBps] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
  const shortSession = (tokenValue) => tokenValue ? `${tokenValue.slice(0, 18)}...` : '—';
  const scenarioOwnerLabel = (scenario) => scenario.full_name || scenario.email || 'Anonimo';
  const scenarioChannelLabel = (scenario) => {
    if (scenario.user_id) return 'Utente registrato';
    return `Anonimo (${shortSession(scenario.anonymous_session_token)})`;
  };

  const tabs = [
    { key: 'users', label: 'Utenti', count: users.length },
    { key: 'scenarios', label: 'Scenari', count: scenarios.length },
    { key: 'capex', label: 'Preventivi', count: capex.length },
    { key: 'business-plans', label: 'Business Plan', count: bps.length },
    { key: 'discovery-intakes', label: 'Discovery', count: intakes.length },
    { key: 'followup', label: '✉️ Follow-up', count: null },
  ];

  const analytics = useMemo(() => buildAdminAnalytics({ users, scenarios, capex, bps, intakes }), [users, scenarios, capex, bps, intakes]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [u, s, c, b, d] = await Promise.all([
        api.get('/api/admin/users'),
        api.get('/api/admin/scenarios'),
        api.get('/api/admin/capex'),
        api.get('/api/admin/business-plans'),
        api.get('/api/admin/discovery-intakes')
      ]);
      setUsers(u.data || []);
      setScenarios(s.data || []);
      setCapex(c.data || []);
      setBps(b.data || []);
      setIntakes(d.data || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Errore caricamento dati admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadAll();
  }, [token]);

  const openDetail = async (type, id) => {
    setLoadingDetail(true);
    setDetailType(type);
    setDetail(null);
    try {
      const r = await api.get(`/api/admin/${type}/${id}`);
      setDetail(r.data || null);
    } catch (err) {
      console.error(err);
      setDetail({ error: err.response?.data?.error || 'Errore caricamento dettaglio' });
    } finally {
      setLoadingDetail(false);
    }
  };

  const changeUserRole = async (userId, role) => {
    try {
      await api.patch(`/api/admin/users/${userId}/role`, { role });
      await loadAll();
      if (detailType === 'users' && detail?.id === userId) {
        await openDetail('users', userId);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Impossibile aggiornare il ruolo');
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(`Confermi eliminazione utente ${u.email}? Questa azione elimina anche i dati collegati.`);
    if (!ok) return;

    try {
      await api.delete(`/api/admin/users/${u.id}`);
      if (detailType === 'users' && detail?.id === u.id) {
        setDetail(null);
        setDetailType(null);
      }
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Impossibile eliminare utente');
    }
  };

  const toolKey = location.pathname.startsWith('/admin/capex') ? 'capex'
    : location.pathname.startsWith('/admin/opex') ? 'opex'
    : location.pathname.startsWith('/admin/business-plan') ? 'business-plan'
    : location.pathname.startsWith('/admin/wiki') ? 'wiki'
    : location.pathname.startsWith('/admin/settings') ? 'settings'
    : location.pathname.startsWith('/admin/followup') ? 'followup'
    : location.pathname.startsWith('/admin/partner') ? 'partner'
    : location.pathname.startsWith('/admin/faq') ? 'faq'
    : location.pathname.startsWith('/admin/content') ? 'content'
    : 'admin-home';

  const sidebarTools = [
    { key: 'admin-home', label: 'Dashboard Admin', to: '/admin' },
    { key: 'capex', label: 'Quote Configurator', to: '/admin/capex' },
    { key: 'opex', label: 'Operating Costs', to: '/admin/opex' },
    { key: 'business-plan', label: 'Business Plan', to: '/admin/business-plan' },
    { key: 'wiki', label: 'Regulatory Wiki', to: '/admin/wiki' },
    { key: 'settings', label: 'Settings', to: '/admin/settings' },
    { key: 'followup', label: '✉️ Follow-up', to: '/admin/followup' },
    { key: 'partner', label: '🏢 Aziende Partner', to: '/admin/partner' },
    { key: 'faq', label: '❓ Gestione FAQ', to: '/admin/faq' },
    { key: 'content', label: '📁 Materiali Commerciali', to: '/admin/content' },
  ];

  const renderToolView = () => {
    if (toolKey === 'capex') return <CapexConfigurator />;
    if (toolKey === 'opex') return <OpexCalculator />;
    if (toolKey === 'business-plan') return <BusinessPlan />;
    if (toolKey === 'wiki') return <Wiki />;
    if (toolKey === 'settings') return <Settings />;
    if (toolKey === 'followup') return <FollowupPage />;
    if (toolKey === 'partner') return <AdminPartnerSection token={token} />;
    if (toolKey === 'faq') return <AdminFAQSection token={token} />;
    if (toolKey === 'content') return <AdminContentSection token={token} />;
    return null;
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <aside style={{ width: 260, background: 'white', borderRight: '1px solid #e5e7eb', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ padding: '0.5rem' }}>
          <img src="/images/drones/logo.png" alt="FlyingBasket" style={{ height: 38 }} />
          <div style={{ marginTop: '0.5rem', fontWeight: 800, color: '#1e3a8a' }}>Admin Panel</div>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{user?.email}</div>
        </div>

        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {sidebarTools.map((item) => (
            <button
              key={item.key}
              onClick={() => navigate(item.to)}
              style={{
                textAlign: 'left',
                padding: '0.65rem 0.75rem',
                borderRadius: 8,
                border: '1px solid #dbeafe',
                background: toolKey === item.key ? '#dbeafe' : '#f8fafc',
                color: '#1e3a8a',
                fontWeight: toolKey === item.key ? 700 : 500,
                cursor: 'pointer'
              }}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => { logout(); navigate('/login'); }}
            style={{ textAlign: 'left', width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontWeight: 600, cursor: 'pointer', marginTop: '0.35rem' }}
          >
            🚪 Logout
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, padding: '1.25rem' }}>
        {toolKey !== 'admin-home' ? (
          <div style={{ background: ['partner','faq','content'].includes(toolKey) ? 'transparent' : '#f4f6fd', borderRadius: 12, padding: ['partner','faq','content'].includes(toolKey) ? '0' : undefined }}>
            {renderToolView()}
          </div>
        ) : (
          <>
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: '1rem', marginBottom: '1rem' }}>
          <h1 style={{ margin: 0, color: '#1e3a8a' }}>Dashboard Admin</h1>
          <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.88rem' }}>Controlla utenti, scenari, preventivi, business plan e risposte discovery.</p>
        </div>

        {error && (
          <div style={{ marginBottom: '1rem', background: '#fef2f2', color: '#b91c1c', padding: '0.75rem 0.9rem', borderRadius: 8, border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        <AdminAnalytics analytics={analytics} loading={loading} />

        <div style={{ display: 'grid', gridTemplateColumns: detail ? '1fr 380px' : '1fr', gap: '1rem', alignItems: 'start' }}>
          <section style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '0.35rem', padding: '0.75rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setDetail(null); setDetailType(null); }}
                  style={{
                    border: '1px solid #cbd5e1',
                    borderRadius: 999,
                    background: tab === t.key ? '#1d4ed8' : 'white',
                    color: tab === t.key ? 'white' : '#334155',
                    fontWeight: 700,
                    padding: '0.35rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  {t.label} ({t.count})
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ padding: '1rem', color: '#64748b' }}>Caricamento dati...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {tab === 'users' && (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Email', 'Nome', 'Azienda', 'Ruolo', 'Creato il', 'Azioni'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td style={tdStyle}>{u.email}</td>
                          <td style={tdStyle}>{u.full_name || '—'}</td>
                          <td style={tdStyle}>{u.company || '—'}</td>
                          <td style={tdStyle}>
                            <select
                              value={u.role || 'user'}
                              onChange={(e) => changeUserRole(u.id, e.target.value)}
                              style={{ padding: '0.3rem 0.45rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                            >
                              <option value="user">user</option>
                              <option value="admin">admin</option>
                              <option value="partner">partner</option>
                            </select>
                          </td>
                          <td style={tdStyle}>{fmt(u.created_at)}</td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '0.35rem' }}>
                              <button
                                onClick={() => openDetail('users', u.id)}
                                style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: 8, padding: '0.32rem 0.55rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}
                              >
                                Dettagli
                              </button>
                              <button
                                onClick={() => deleteUser(u)}
                                style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', borderRadius: 8, padding: '0.32rem 0.55rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}
                              >
                                Elimina
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {tab === 'scenarios' && (
                  <SimpleTable
                    headers={['Canale', 'Utente', 'Email', 'Tipo', 'Origine', 'Nome Scenario', 'Creato il', 'Azioni']}
                    rows={scenarios.map((s) => [scenarioChannelLabel(s), scenarioOwnerLabel(s), s.email || '—', s.scenario_type || '—', s.source || 'app', s.name, fmt(s.created_at), (
                      <button key="d" onClick={() => openDetail('scenarios', s.id)} style={btnInfo}>Dettagli</button>
                    )])}
                  />
                )}

                {tab === 'capex' && (
                  <SimpleTable
                    headers={['Utente', 'Email', 'Nome', 'CAPEX Totale', 'Creato il', 'Azioni']}
                    rows={capex.map((c) => [c.full_name, c.email, c.name || '—', c.total_capex != null ? `${c.total_capex} €` : '—', fmt(c.created_at), (
                      <button key="d" onClick={() => openDetail('capex', c.id)} style={btnInfo}>Dettagli</button>
                    )])}
                  />
                )}

                {tab === 'business-plans' && (
                  <SimpleTable
                    headers={['Utente', 'Email', 'Titolo', 'Break-even', 'Creato il', 'Azioni']}
                    rows={bps.map((b) => [b.full_name, b.email, b.title || '—', b.breakeven_month || '—', fmt(b.created_at), (
                      <button key="d" onClick={() => openDetail('business-plans', b.id)} style={btnInfo}>Dettagli</button>
                    )])}
                  />
                )}

                {tab === 'discovery-intakes' && (
                  <SimpleTable
                    headers={['Utente', 'Email', 'Payload', 'Kg', 'Km', 'Creato il', 'Azioni']}
                    rows={intakes.map((d) => [d.full_name, d.email, d.payload_description || '—', d.payload_weight_kg || '—', d.distance_km || '—', fmt(d.created_at), (
                      <button key="d" onClick={() => openDetail('discovery-intakes', d.id)} style={btnInfo}>Dettagli</button>
                    )])}
                  />
                )}

                {tab === 'followup' && <FollowupPage />}
              </div>
            )}
          </section>

          {detail && (
            <aside style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: '1rem', position: 'sticky', top: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                <h3 style={{ margin: 0, color: '#1e3a8a', fontSize: '1rem' }}>
                  {detailType === 'users' ? 'Dettaglio Utente' : 'Dettaglio Record'}
                </h3>
                <button onClick={() => { setDetail(null); setDetailType(null); }} style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 8, padding: '0.25rem 0.45rem', cursor: 'pointer' }}>Chiudi</button>
              </div>

              {loadingDetail ? (
                <div style={{ color: '#64748b', fontSize: '0.86rem' }}>Caricamento dettaglio...</div>
              ) : detail.error ? (
                <div style={{ color: '#b91c1c', fontSize: '0.86rem' }}>{detail.error}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                  {Object.entries(detail).map(([k, v]) => (
                    <div key={k} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.55rem 0.6rem' }}>
                      <div style={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.2rem' }}>{k}</div>
                      <div style={{ fontSize: '0.82rem', color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v ?? '—')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          )}
        </div>
          </>
        )}
      </main>
    </div>
  );
}

function AdminAnalytics({ analytics, loading }) {
  const metricCards = [
    {
      label: 'Utenti registrati',
      value: formatNumber(analytics.userCount),
      detail: `${formatPercent(analytics.usersWithScenarios, analytics.userCount)} con scenari, ${formatPercent(analytics.usersWithCapex, analytics.userCount)} con preventivi`,
      Icon: UsersIcon,
      accent: '#1d4ed8'
    },
    {
      label: 'Scenari testati',
      value: formatNumber(analytics.scenarioCount),
      detail: `${formatPercent(analytics.registeredScenarios, analytics.scenarioCount)} registrati, ${formatPercent(analytics.anonymousScenarios, analytics.scenarioCount)} anonimi`,
      Icon: Zap,
      accent: '#0f766e'
    },
    {
      label: 'Preventivi',
      value: formatNumber(analytics.capexCount),
      detail: `${formatCurrency(analytics.totalCapex)} valore totale testato`,
      Icon: Package,
      accent: '#7c3aed'
    },
    {
      label: 'Business plan',
      value: formatNumber(analytics.businessPlanCount),
      detail: analytics.avgBreakeven ? `Break-even medio ${formatNumber(analytics.avgBreakeven, 1)} mesi` : 'Nessun break-even calcolato',
      Icon: TrendingUp,
      accent: '#16a34a'
    },
    {
      label: 'Payload più testato',
      value: analytics.topPayload?.name || '—',
      detail: analytics.topPayload ? `${formatNumber(analytics.topPayload.count)} test, ${analytics.topPayload.percentLabel}` : 'In attesa di scenari',
      Icon: Target,
      accent: '#db2777'
    },
    {
      label: 'Distanza più testata',
      value: analytics.topDistance?.name || '—',
      detail: analytics.topDistance ? `${formatNumber(analytics.topDistance.count)} test, ${analytics.topDistance.percentLabel}` : 'In attesa di scenari',
      Icon: Route,
      accent: '#ea580c'
    }
  ];

  return (
    <section style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: '#0f172a', fontWeight: 800 }}>
            <BarChart3 size={20} color="#1d4ed8" />
            <span>Analytics aggregate</span>
          </div>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.86rem' }}>
            Percentuali, frequenze e valori medi ricavati da utenti, scenari e preventivi.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', background: '#ecfeff', color: '#155e75', border: '1px solid #a5f3fc', borderRadius: 8, padding: '0.45rem 0.65rem', fontSize: '0.78rem', fontWeight: 700 }}>
          <Percent size={15} />
          <span>{analytics.signalLine}</span>
        </div>
      </div>

      {loading ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1rem', color: '#64748b' }}>
          Caricamento analytics...
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
            {metricCards.map((card) => (
              <MetricCard key={card.label} {...card} />
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <ChartPanel title="Attività nel tempo" subtitle="Record creati per giorno">
              {analytics.activityData.length ? (
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.activityData} margin={{ top: 12, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip content={<AdminChartTooltip />} />
                      <Line type="monotone" dataKey="scenari" name="Scenari" stroke="#1d4ed8" strokeWidth={2.5} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="preventivi" name="Preventivi" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="utenti" name="Utenti" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="businessPlan" name="Business plan" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="discovery" name="Discovery" stroke="#db2777" strokeWidth={2.5} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics label="Nessuna attività disponibile" />}
            </ChartPanel>

            <ChartPanel title="Tipi di scenario" subtitle="Distribuzione degli scenari OPEX">
              {analytics.scenarioTypeData.length ? (
                <>
                  <div style={{ height: 210 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={analytics.scenarioTypeData} dataKey="count" nameKey="name" innerRadius={54} outerRadius={82} paddingAngle={2}>
                          {analytics.scenarioTypeData.map((entry, index) => (
                            <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<AdminChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ChartLegend data={analytics.scenarioTypeData} />
                </>
              ) : <EmptyAnalytics label="Nessuno scenario da aggregare" />}
            </ChartPanel>

            <ChartPanel title="Payload testati" subtitle="Peso per singolo lift o pannello">
              <RangeBarChart data={analytics.payloadRangeData} />
            </ChartPanel>

            <ChartPanel title="Distanze testate" subtitle="Distanza missione o tratta operativa">
              <RangeBarChart data={analytics.distanceRangeData} />
            </ChartPanel>

            <ChartPanel title="Modelli richiesti" subtitle="Droni scelti negli scenari e nei preventivi">
              <RangeBarChart data={analytics.droneModelData} />
            </ChartPanel>

            <ChartPanel title="Extra e training" subtitle="Opzioni selezionate nei preventivi">
              <RangeBarChart data={analytics.extrasData} />
            </ChartPanel>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '0.75rem' }}>
            <ChartPanel title="Segnali più frequenti" subtitle="Le scelte che tornano più spesso">
              <ProgressList items={analytics.frequentSignals} />
            </ChartPanel>

            <ChartPanel title="Statistiche operative" subtitle="Medie e mediane dei numeri testati">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.6rem' }}>
                {analytics.operationalStats.map((stat) => (
                  <div key={stat.label} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.65rem', background: '#f8fafc', minHeight: 86 }}>
                    <div style={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{stat.label}</div>
                    <div style={{ color: '#0f172a', fontSize: '1.1rem', fontWeight: 800, marginTop: '0.25rem', wordBreak: 'break-word' }}>{stat.value}</div>
                    <div style={{ color: '#64748b', fontSize: '0.76rem', marginTop: '0.2rem' }}>{stat.detail}</div>
                  </div>
                ))}
              </div>
            </ChartPanel>
          </div>
        </>
      )}
    </section>
  );
}

function MetricCard({ label, value, detail, Icon, accent }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.85rem', minHeight: 118, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start' }}>
        <div style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 800 }}>{label}</div>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: `${accent}14`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <Icon size={18} />
        </div>
      </div>
      <div>
        <div style={{ color: '#0f172a', fontSize: '1.45rem', lineHeight: 1.1, fontWeight: 850, wordBreak: 'break-word' }}>{value}</div>
        <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '0.3rem' }}>{detail}</div>
      </div>
    </div>
  );
}

function ChartPanel({ title, subtitle, children }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.85rem', minHeight: 280 }}>
      <div style={{ marginBottom: '0.65rem' }}>
        <h3 style={{ margin: 0, color: '#0f172a', fontSize: '0.98rem' }}>{title}</h3>
        <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '0.15rem' }}>{subtitle}</div>
      </div>
      {children}
    </div>
  );
}

function RangeBarChart({ data }) {
  if (!data.length) return <EmptyAnalytics label="Dati insufficienti" />;

  return (
    <div style={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 14, left: 16, bottom: 0 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
          <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 11, fill: '#475569' }} />
          <Tooltip content={<AdminChartTooltip />} />
          <Bar dataKey="count" name="Test" radius={[0, 6, 6, 0]}>
            {data.map((entry, index) => (
              <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProgressList({ items }) {
  if (!items.length) return <EmptyAnalytics label="Nessun segnale disponibile" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
      {items.map((item, index) => (
        <div key={`${item.label}-${item.value}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline', marginBottom: '0.28rem' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</div>
              <div style={{ color: '#0f172a', fontWeight: 800, fontSize: '0.92rem', wordBreak: 'break-word' }}>{item.value}</div>
            </div>
            <div style={{ color: chartColors[index % chartColors.length], fontWeight: 850, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{item.percentLabel}</div>
          </div>
          <div style={{ height: 7, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
            <div style={{ width: `${Math.max(4, item.percent)}%`, height: '100%', background: chartColors[index % chartColors.length], borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ChartLegend({ data }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.45rem' }}>
      {data.map((entry, index) => (
        <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#475569', fontSize: '0.78rem' }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: chartColors[index % chartColors.length], flex: '0 0 auto' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
          <strong style={{ color: '#0f172a', marginLeft: 'auto' }}>{entry.percentLabel}</strong>
        </div>
      ))}
    </div>
  );
}

function AdminChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const title = label || payload[0]?.payload?.name;

  return (
    <div style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: 8, padding: '0.55rem 0.65rem', boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)' }}>
      <div style={{ color: '#0f172a', fontWeight: 800, fontSize: '0.78rem', marginBottom: '0.25rem' }}>{title}</div>
      {payload.map((item) => (
        <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', color: '#475569', fontSize: '0.75rem' }}>
          <span>{item.name}</span>
          <strong style={{ color: item.color || '#0f172a' }}>
            {formatNumber(item.value)}{item.payload?.percentLabel ? ` (${item.payload.percentLabel})` : ''}
          </strong>
        </div>
      ))}
    </div>
  );
}

function EmptyAnalytics({ label }) {
  return (
    <div style={{ minHeight: 180, border: '1px dashed #cbd5e1', borderRadius: 8, background: '#f8fafc', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', textAlign: 'center', fontSize: '0.84rem' }}>
      {label}
    </div>
  );
}

function buildAdminAnalytics({ users, scenarios, capex, bps, intakes }) {
  const userIdsWithScenarios = new Set(scenarios.filter((s) => s.user_id).map((s) => s.user_id));
  const userIdsWithCapex = new Set(capex.filter((c) => c.user_id).map((c) => c.user_id));
  const registeredScenarios = scenarios.filter((s) => !!s.user_id).length;
  const anonymousScenarios = scenarios.length - registeredScenarios;

  const scenarioInsights = scenarios.map((scenario) => {
    const params = parseJsonField(scenario.input_params);
    const results = parseJsonField(scenario.results);
    const type = inferScenarioType(scenario, params);
    const payload = scenarioPayloadKg(params, type);
    const distance = scenarioDistanceMeters(params, type);
    const totalLoad = scenarioTotalLoadKg(params, type);
    const dailyCost = firstNumber(scenario.daily_cost, results?.summary?.totalCost);
    const droneModel = params.droneModel || params.drone_model || null;
    const capexMode = params.capexMode || (scenario.config_id || params.linkedCapexId ? 'linked' : 'manual');
    const feasible = typeof results?.feasible === 'boolean'
      ? results.feasible
      : (typeof results?.operations?.feasible === 'boolean' ? results.operations.feasible : null);

    return {
      ...scenario,
      params,
      results,
      type,
      typeLabel: scenarioTypeLabel(type),
      payload,
      distance,
      totalLoad,
      dailyCost,
      droneModel,
      capexMode,
      feasible
    };
  });

  const capexInsights = capex.map((quote) => ({
    ...quote,
    totalCapex: toNumber(quote.total_capex),
    leasingMonthly: toNumber(quote.leasing_monthly),
    droneModel: quote.drone_model || null,
    numDrones: toNumber(quote.num_drones),
    packageTier: quote.package_tier || null,
    authorizationSupport: quote.authorization_support || null,
    connectivityPackage: quote.connectivity_package || null
  }));

  const businessPlanInsights = bps.map((plan) => ({
    ...plan,
    breakeven: toNumber(plan.breakeven_month),
    roi60: toNumber(plan.roi_60m),
    monthlyRevenue: toNumber(plan.monthly_revenue),
    monthlyOpex: toNumber(plan.monthly_opex)
  }));

  const scenarioTypeData = countBy(scenarioInsights, (s) => s.typeLabel);
  const sourceData = countBy(scenarioInsights, (s) => sourceLabel(s.source));
  const scenarioDroneModelData = countBy(scenarioInsights, (s) => s.droneModel);
  const quoteDroneModelData = countBy(capexInsights, (c) => c.droneModel);
  const droneModelData = countBy(
    [
      ...scenarioInsights.map((s) => s.droneModel).filter(Boolean),
      ...capexInsights.map((c) => c.droneModel).filter(Boolean)
    ],
    (model) => model
  );

  const payloadValues = scenarioInsights.map((s) => s.payload).filter(isFiniteNumber);
  const distanceValues = scenarioInsights.map((s) => s.distance).filter(isFiniteNumber);
  const totalLoadValues = scenarioInsights.map((s) => s.totalLoad).filter(isFiniteNumber);
  const dailyCostValues = scenarioInsights.map((s) => s.dailyCost).filter(isFiniteNumber);
  const capexValues = capexInsights.map((c) => c.totalCapex).filter(isFiniteNumber);
  const quoteDroneCounts = capexInsights.map((c) => c.numDrones).filter(isFiniteNumber);

  const payloadRangeData = bucketize(payloadValues, [
    { name: '<=25 kg', max: 25 },
    { name: '26-50 kg', min: 25, max: 50 },
    { name: '51-100 kg', min: 50, max: 100 },
    { name: '101-200 kg', min: 100, max: 200 },
    { name: '>200 kg', min: 200 }
  ]);

  const distanceRangeData = bucketize(distanceValues, [
    { name: '<=500 m', max: 500 },
    { name: '0,5-1 km', min: 500, max: 1000 },
    { name: '1-3 km', min: 1000, max: 3000 },
    { name: '3-5 km', min: 3000, max: 5000 },
    { name: '>5 km', min: 5000 }
  ]);

  const extrasData = buildExtrasData(capexInsights);
  const packageData = countBy(capexInsights, (c) => packageLabel(c.packageTier));
  const authData = countBy(capexInsights, (c) => authLabel(c.authorizationSupport));
  const capexModeData = countBy(scenarioInsights, (s) => capexModeLabel(s.capexMode));
  const feasibleValues = scenarioInsights.filter((s) => s.feasible !== null);
  const notFeasibleCount = feasibleValues.filter((s) => s.feasible === false).length;

  const totalCapex = sum(capexValues);
  const avgBreakeven = average(businessPlanInsights.map((b) => b.breakeven).filter(isFiniteNumber));
  const topPayload = topItem(payloadRangeData);
  const topDistance = topItem(distanceRangeData);
  const topScenario = topItem(scenarioTypeData);
  const topQuoteModel = topItem(quoteDroneModelData);
  const topScenarioModel = topItem(scenarioDroneModelData);
  const topPackage = topItem(packageData);
  const topExtra = topItem(extrasData);
  const topSource = topItem(sourceData);
  const topCapexMode = topItem(capexModeData);
  const linkedScenarios = scenarioInsights.filter((s) => s.capexMode === 'linked' || s.config_id || s.params?.linkedCapexId).length;

  const frequentSignals = [
    signalItem('Scenario più testato', topScenario),
    signalItem('Canale più frequente', topSource),
    signalItem('Drone negli scenari', topScenarioModel),
    signalItem('Drone nei preventivi', topQuoteModel),
    signalItem('Pacchetto preventivo', topPackage),
    signalItem('Autorizzazione quote', topItem(authData)),
    signalItem('Modalità CAPEX OPEX', topCapexMode),
    signalItem('Extra più richiesto', topExtra)
  ].filter(Boolean);

  const operationalStats = [
    {
      label: 'Payload medio',
      value: payloadValues.length ? `${formatNumber(average(payloadValues), 1)} kg` : '—',
      detail: payloadValues.length ? `Mediana ${formatNumber(median(payloadValues), 1)} kg` : 'Nessun payload'
    },
    {
      label: 'Distanza media',
      value: distanceValues.length ? formatMeters(average(distanceValues)) : '—',
      detail: distanceValues.length ? `Mediana ${formatMeters(median(distanceValues))}` : 'Nessuna distanza'
    },
    {
      label: 'Carico totale',
      value: totalLoadValues.length ? `${formatNumber(average(totalLoadValues), 1)} kg` : '—',
      detail: totalLoadValues.length ? `Mediana ${formatNumber(median(totalLoadValues), 1)} kg` : 'Nessun carico'
    },
    {
      label: 'Costo OPEX',
      value: dailyCostValues.length ? formatCurrency(average(dailyCostValues)) : '—',
      detail: dailyCostValues.length ? `Mediana ${formatCurrency(median(dailyCostValues))}` : 'Nessun costo'
    },
    {
      label: 'Preventivo medio',
      value: capexValues.length ? formatCurrency(average(capexValues)) : '—',
      detail: capexValues.length ? `Mediana ${formatCurrency(median(capexValues))}` : 'Nessun preventivo'
    },
    {
      label: 'Droni per quote',
      value: quoteDroneCounts.length ? `${formatNumber(average(quoteDroneCounts), 1)}` : '—',
      detail: quoteDroneCounts.length ? `Più frequente ${mode(quoteDroneCounts) || '—'}` : 'Nessuna flotta'
    },
    {
      label: 'Scenario con quote',
      value: scenarios.length ? formatPercent(linkedScenarios, scenarios.length) : '—',
      detail: `${formatNumber(linkedScenarios)} scenari collegati`
    },
    {
      label: 'Non fattibili',
      value: feasibleValues.length ? formatPercent(notFeasibleCount, feasibleValues.length) : '—',
      detail: feasibleValues.length ? `${formatNumber(notFeasibleCount)} su ${formatNumber(feasibleValues.length)} calcoli` : 'Nessun esito'
    }
  ];

  const activityData = buildActivityData([
    ...users.map((u) => ({ date: u.created_at, kind: 'utenti' })),
    ...scenarios.map((s) => ({ date: s.created_at, kind: 'scenari' })),
    ...capex.map((c) => ({ date: c.created_at, kind: 'preventivi' })),
    ...bps.map((b) => ({ date: b.created_at, kind: 'businessPlan' })),
    ...intakes.map((d) => ({ date: d.created_at, kind: 'discovery' }))
  ]);

  return {
    userCount: users.length,
    scenarioCount: scenarios.length,
    capexCount: capex.length,
    businessPlanCount: bps.length,
    usersWithScenarios: userIdsWithScenarios.size,
    usersWithCapex: userIdsWithCapex.size,
    registeredScenarios,
    anonymousScenarios,
    totalCapex,
    avgBreakeven,
    topPayload,
    topDistance,
    scenarioTypeData,
    payloadRangeData,
    distanceRangeData,
    droneModelData,
    extrasData,
    frequentSignals,
    operationalStats,
    activityData,
    signalLine: topScenario ? `${topScenario.name}: ${topScenario.percentLabel} degli scenari` : 'In attesa di dati'
  };
}

const btnInfo = {
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: 8,
  padding: '0.32rem 0.55rem',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '0.78rem'
};

function SimpleTable({ headers, rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {headers.map((h) => <th key={h} style={thStyle}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => <td key={j} style={tdStyle}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function parseJsonField(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return {};
  try {
    return JSON.parse(value);
  } catch (_) {
    return {};
  }
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function firstNumber(...values) {
  for (const value of values) {
    const n = toNumber(value);
    if (n !== null) return n;
  }
  return null;
}

function isFiniteNumber(value) {
  return Number.isFinite(value);
}

function inferScenarioType(scenario, params) {
  const explicit = params?.calculatorType || params?.scenarioType || scenario?.scenario_type;
  if (explicit === 'single_route') return 'single_route';
  if (explicit === 'solar_panel') return 'solar_panel';
  if (explicit === 'middle_mile') return 'middle_mile';
  if (Object.prototype.hasOwnProperty.call(params || {}, 'payloadKg') && !Object.prototype.hasOwnProperty.call(params || {}, 'totalKg')) return 'single_route';
  if (Object.prototype.hasOwnProperty.call(params || {}, 'totalPanels') || Object.prototype.hasOwnProperty.call(params || {}, 'panelWeight')) return 'solar_panel';
  return explicit || 'unknown';
}

function scenarioTypeLabel(type) {
  if (type === 'single_route') return 'Single Route';
  if (type === 'solar_panel') return 'Solar Panel';
  if (type === 'middle_mile') return 'Middle Mile';
  return 'Altro';
}

function sourceLabel(source) {
  if (source === 'landing') return 'Landing anonima';
  if (source === 'app') return 'App';
  return source || 'Non definita';
}

function scenarioPayloadKg(params, type) {
  if (type === 'single_route') return firstNumber(params?.payloadKg, params?.maxWeightPerTrip);
  if (type === 'solar_panel') return firstNumber(params?.panelWeight, params?.payloadKg);
  if (type === 'middle_mile') return firstNumber(params?.maxWeightPerTrip, params?.payloadKg, params?.totalKg);
  return firstNumber(params?.payloadKg, params?.maxWeightPerTrip, params?.panelWeight, params?.totalKg);
}

function scenarioDistanceMeters(params, type) {
  if (type === 'solar_panel') return firstNumber(params?.groundDistance, params?.distance);
  return firstNumber(params?.distance, params?.groundDistance);
}

function scenarioTotalLoadKg(params, type) {
  if (type === 'solar_panel') {
    const panels = toNumber(params?.totalPanels);
    const panelWeight = toNumber(params?.panelWeight);
    if (panels !== null && panelWeight !== null) return panels * panelWeight;
  }
  if (type === 'middle_mile') return firstNumber(params?.totalKg, params?.maxWeightPerTrip);
  return firstNumber(params?.payloadKg, params?.totalKg);
}

function countBy(items, getKey) {
  const counts = new Map();
  items.forEach((item) => {
    const key = getKey(item);
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const total = [...counts.values()].reduce((acc, value) => acc + value, 0);
  return [...counts.entries()]
    .map(([name, count]) => ({
      name,
      count,
      percent: total ? (count / total) * 100 : 0,
      percentLabel: total ? `${formatNumber((count / total) * 100)}%` : '0%'
    }))
    .sort((a, b) => b.count - a.count);
}

function bucketize(values, ranges) {
  const total = values.length;
  if (!total) return [];
  return ranges
    .map((range) => {
      const count = values.filter((value) => {
        const minOk = range.min == null || value > range.min;
        const maxOk = range.max == null || value <= range.max;
        return minOk && maxOk;
      }).length;
      return {
        name: range.name,
        count,
        percent: total ? (count / total) * 100 : 0,
        percentLabel: total ? `${formatNumber((count / total) * 100)}%` : '0%'
      };
    })
    .filter((item) => item.count > 0);
}

function buildExtrasData(capexInsights) {
  const extras = [
    ['Starlink', 'has_starlink'],
    ['Trailer', 'has_trailer'],
    ['Bottom hook', 'has_bottom_hook'],
    ['Winch', 'has_winch'],
    ['LiDAR', 'has_lidar'],
    ['Training A2', 'training_a2'],
    ['Training STS', 'training_sts'],
    ['Comm + CRM', 'training_comm_crm'],
    ['SORA', 'training_sora'],
    ['Piloting', 'training_piloting'],
    ['Maintenance', 'training_maintenance']
  ];
  const total = capexInsights.length;
  if (!total) return [];

  return extras
    .map(([name, field]) => {
      const count = capexInsights.filter((quote) => !!quote[field]).length;
      return {
        name,
        count,
        percent: total ? (count / total) * 100 : 0,
        percentLabel: total ? `${formatNumber((count / total) * 100)}%` : '0%'
      };
    })
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);
}

function buildActivityData(records) {
  const byDay = new Map();
  records.forEach((record) => {
    const date = new Date(record.date);
    if (Number.isNaN(date.getTime())) return;
    const key = date.toISOString().slice(0, 10);
    if (!byDay.has(key)) {
      byDay.set(key, {
        sortKey: key,
        day: date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
        utenti: 0,
        scenari: 0,
        preventivi: 0,
        businessPlan: 0,
        discovery: 0
      });
    }
    byDay.get(key)[record.kind] += 1;
  });

  return [...byDay.values()]
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(-14);
}

function packageLabel(value) {
  if (value === 'fb3_pro') return 'FB3 Standard';
  if (value === 'fb3_max_ultra') return 'FB3 Pro';
  if (value === 'fb3_base') return 'FB3 Essential';
  return value || null;
}

function authLabel(value) {
  if (value === 'bvlos') return 'BVLOS';
  if (value === 'vlos') return 'VLOS';
  return value || null;
}

function capexModeLabel(value) {
  if (value === 'linked') return 'Preventivo salvato';
  if (value === 'advisor') return 'Advisor';
  if (value === 'manual') return 'Manuale';
  return value || null;
}

function signalItem(label, item) {
  if (!item) return null;
  return {
    label,
    value: item.name,
    count: item.count,
    percent: item.percent,
    percentLabel: item.percentLabel
  };
}

function topItem(data) {
  return data.find((item) => item.count > 0) || null;
}

function sum(values) {
  return values.reduce((acc, value) => acc + value, 0);
}

function average(values) {
  if (!values.length) return null;
  return sum(values) / values.length;
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function mode(values) {
  if (!values.length) return null;
  return topItem(countBy(values, (value) => String(value)))?.name || null;
}

function formatNumber(value, maximumFractionDigits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('it-IT', { maximumFractionDigits }).format(n);
}

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(n);
}

function formatPercent(part, total) {
  if (!total) return '0%';
  return `${formatNumber((part / total) * 100)}%`;
}

function formatMeters(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return `${formatNumber(n / 1000, 1)} km`;
  return `${formatNumber(n)} m`;
}
